class AudioManager {
  constructor() {
    this.audioContext = null;
    this.buffers = new Map();
    this.volume = this.loadVolumeSetting();
    this.audioInitialized = false;
    // Default rates and volumes for UI sounds
    this.soundSettings = {
      'SELECT': { volume: 0.5, rate: 1.4 },
      'PREV': { volume: 0.5, rate: 1.0 },
      'NEXT': { volume: 0.5, rate: 1.2 },
      'HOVER': { volume: 0.2, rate: 3.0 },
      'SUCCESS': { volume: 0.5, rate: 1.0 },
      'FAIL': { volume: 0.6, rate: 1.0 }
    };
  }

  loadVolumeSetting() {
    try {
      const savedVolume = localStorage.getItem('neuranet-volume');
      return savedVolume !== null ? parseFloat(savedVolume) : 1.0;
    } catch (error) {
      console.error('Error loading volume setting:', error);
      return 1.0;
    }
  }

  saveVolumeSetting(volume) {
    try {
      localStorage.setItem('neuranet-volume', volume.toString());
    } catch (error) {
      console.error('Error saving volume setting:', error);
    }
  }

  async fetchAndCacheAudio(path) {
    const cacheName = 'neuranet-audio-cache';
    try {
      // Open cache
      const cache = await caches.open(cacheName);
      
      // Try to get from cache first
      let response = await cache.match(path);
      
      if (!response) {
        // If not in cache, fetch it
        response = await fetch(path);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // Clone the response before caching because response body can only be used once
        const responseToCache = response.clone();
        await cache.put(path, responseToCache);
      }
      
      return await response.arrayBuffer();
    } catch (error) {
      console.error(`Error fetching/caching audio: ${path}`, error);
      throw error;
    }
  }

  async init() {
    if (this.audioInitialized) return;

    try {
      // Create AudioContext on user interaction to comply with browser policies
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Use R2 URL instead of local files
      const getAudioPath = (filename) => `${process.env.REACT_APP_AUDIO_BASE_URL}/${filename}`;
      
      const audioFiles = {
        // Original audio files
        '0': getAudioPath('0.mp3'),
        '1': getAudioPath('1.mp3'),
        '2': getAudioPath('2.mp3'),
        '3': getAudioPath('3.mp3'),
        '4': getAudioPath('4.mp3'),
        '5': getAudioPath('5.mp3'),
        '6': getAudioPath('6.mp3'),
        '7': getAudioPath('7.mp3'),
        '8': getAudioPath('8.mp3'),
        '9': getAudioPath('9.mp3'),
        'A': getAudioPath('a.mp3'),
        'B': getAudioPath('b.mp3'),
        'C': getAudioPath('c.mp3'),
        'D': getAudioPath('d.mp3'),
        'E': getAudioPath('e.mp3'),
        'F': getAudioPath('f.mp3'),
        'G': getAudioPath('g.mp3'),
        'H': getAudioPath('h.mp3'),
        'I': getAudioPath('i.mp3'),
        'J': getAudioPath('j.mp3'),
        'K': getAudioPath('k.mp3'),
        'L': getAudioPath('l.mp3'),
        'M': getAudioPath('m.mp3'),
        'N': getAudioPath('n.mp3'),
        'O': getAudioPath('o.mp3'),
        'P': getAudioPath('p.mp3'),
        'Q': getAudioPath('q.mp3'),
        'R': getAudioPath('r.mp3'),
        'S': getAudioPath('s.mp3'),
        'T': getAudioPath('t.mp3'),
        'U': getAudioPath('u.mp3'),
        'V': getAudioPath('v.mp3'),
        'W': getAudioPath('w.mp3'),
        'X': getAudioPath('x.mp3'),
        'Y': getAudioPath('y.mp3'),
        'Z': getAudioPath('z.mp3'),
        'PING': getAudioPath('ping.mp3'),
        'ALERT': getAudioPath('alert.mp3'),
        
        // UI sound effects
        'SELECT': getAudioPath('select.mp3'),
        'PREV': getAudioPath('prev.mp3'),
        'NEXT': getAudioPath('next.mp3'),
        'HOVER': getAudioPath('hover.mp3'),
        'SUCCESS': getAudioPath('success.mp3'),
        'FAIL': getAudioPath('fail.mp3')
      };

      // Load and decode all audio files with caching
      await Promise.all(
        Object.entries(audioFiles).map(async ([char, path]) => {
          try {
            const arrayBuffer = await this.fetchAndCacheAudio(path);
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.buffers.set(char, audioBuffer);
          } catch (error) {
            console.error(`Failed to load audio file ${path}:`, error);
          }
        })
      );

      this.audioInitialized = true;
    } catch (error) {
      console.error('Failed to initialize audio system:', error);
      this.audioInitialized = false;
      throw error;
    }
  }

  async playPing(scheduleTime = 0) {
    if (!this.audioInitialized || !this.buffers.has('PING')) {
      return;
    }

    try {
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = this.buffers.get('PING');
      gainNode.gain.value = this.volume; // Apply master volume

      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Use precise scheduling if a time is provided, ensuring we don't schedule in the past
      let startTime;
      if (scheduleTime > 0) {
        // If the scheduled time is in the past, use current time
        startTime = Math.max(scheduleTime, this.audioContext.currentTime);
      } else {
        startTime = this.audioContext.currentTime;
      }
      source.start(startTime);

      source.onended = () => {
        source.disconnect();
        gainNode.disconnect();
      };
    } catch (error) {
      console.error('Error playing ping sound:', error);
    }
  }

  async playSound(char, customVolume = null, customRate = null) {
    if (!this.audioInitialized || !this.buffers.has(char)) {
      return;
    }

    try {
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = this.buffers.get(char);
      
      // Calculate the final volume by applying master volume as a multiplier
      let baseVolume;
      if (customVolume !== null) {
        baseVolume = customVolume;
      } else if (this.soundSettings[char]) {
        baseVolume = this.soundSettings[char].volume;
      } else {
        baseVolume = 1.0;
      }
      
      // Apply master volume as a multiplier
      gainNode.gain.value = baseVolume * this.volume;
      
      // Use custom rate if provided, otherwise use soundSettings if available
      if (customRate !== null) {
        source.playbackRate.value = customRate;
      } else if (this.soundSettings[char]) {
        source.playbackRate.value = this.soundSettings[char].rate;
      }

      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start(0);

      source.onended = () => {
        source.disconnect();
        gainNode.disconnect();
      };
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  }

  // UI Sound Methods
  async playSelect() {
    await this.playSound('SELECT');
  }

  async playPrev() {
    await this.playSound('PREV');
  }

  async playNext() {
    await this.playSound('NEXT');
  }

  async playHover() {
    await this.playSound('HOVER');
  }

  async playSuccess() {
    await this.playSound('SUCCESS');
  }

  async playFail() {
    await this.playSound('FAIL');
  }

  async playAlert() {
    await this.playSound('ALERT');
  }

  setVolume(value) {
    this.volume = Math.max(0, Math.min(1, value));
    this.saveVolumeSetting(this.volume);
  }

  getVolume() {
    return this.volume;
  }

  isInitialized() {
    return this.audioInitialized;
  }

  async resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }
}

// Export a singleton instance
const audioManager = new AudioManager();
export default audioManager;